/**
 * CalmRedButtonEntry — gentle surfacing of the existing Red Button
 * capability on the calm learner dashboard.
 *
 * Operator brief (Activation Phase 1 #1, Feb 17, 2026 — AP-5
 * surfacing decision approved):
 *   "Surface the existing Red Button / Support Level capability
 *    calmly. NO dramatic emergency UX. NO new backend. NO new
 *    emotional-support system. Just surface the existing
 *    implementation calmly. ~30 LOC."
 *
 * What this does:
 *   • Calls existing GET /api/red-button/today on mount.
 *   • Renders one quiet inline link on the calm home.
 *   • On tap, opens a MobileBottomSheet that mounts the existing
 *     `RedButtonCard` from `pages/LearnerOpeningFlow.js`.
 *   • Hides itself entirely if the learner already activated red
 *     button today (the in-page card on the sheet still works —
 *     calling activate again is idempotent server-side).
 *
 * What this deliberately does NOT do:
 *   • Does not introduce any new backend endpoint.
 *   • Does not emit evidence (emotional state ≠ competency claim).
 *   • Does not show on the instructor side here — that's a
 *     separately-approved follow-on.
 *   • Does not bundle the Support Level multi-step flow — it lives
 *     in `DailyCheckIn` and is out of scope for this minimum
 *     surfacing.
 *
 * Doctrine adherence (per IMPLEMENTED_CAPABILITY_REGISTRY.md):
 *   • Reuses existing backend (no parallel emotional-support
 *     system).
 *   • Reuses existing `RedButtonCard` component (no duplicate
 *     emotional UX).
 *   • Mounts in `MobileBottomSheet` (canonical sheet primitive).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LifeBuoy } from "lucide-react";
import { MobileBottomSheet } from "../mobile";

const API = process.env.REACT_APP_BACKEND_URL;

// Re-import the existing RedButtonCard from LearnerOpeningFlow.
// Tree-shaking will not pull the rest of that page's components in
// at runtime because the named import is unused here — and we
// deliberately use a dynamic import to keep the calm home's bundle
// lean. The card itself is self-contained (props: redButton,
// onActivate, onOnboarded).
import { RedButtonCard } from "../../pages/LearnerOpeningFlow";

export default function CalmRedButtonEntry() {
  const [redButton, setRedButton] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchToday = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/red-button/today`, {
        withCredentials: true,
      });
      setRedButton(r.data);
    } catch {
      setRedButton(null);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // If the API can't be reached at all, hide silently (calm-baseline
  // — never show a broken link).
  if (redButton === null) return null;

  // If the learner has already activated today, hide the link —
  // their instructor already has the signal. Re-opening would be
  // noise.
  if (redButton.active) return null;

  return (
    <>
      <button
        type="button"
        data-testid="calm-home-red-button-entry"
        onClick={() => setOpen(true)}
        className="w-full text-left text-xs text-slate-500 dark:text-slate-400
                   hover:text-slate-700 dark:hover:text-slate-200
                   inline-flex items-center gap-1.5 transition-colors py-1"
      >
        <LifeBuoy className="w-3.5 h-3.5" />
        <span>Need extra support today?</span>
      </button>
      <MobileBottomSheet
        open={open}
        onClose={() => setOpen(false)}
        testid="calm-home-red-button-sheet"
        title="A gentler day"
      >
        <div className="space-y-4 p-1">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Some days don't feel like normal driving days. If you'd like
            today's lesson to be gentler, tap below — your instructor
            will know to take it slower.
          </p>
          <RedButtonCard
            redButton={redButton}
            onActivate={() => {
              fetchToday();
              setOpen(false);
            }}
            onOnboarded={fetchToday}
          />
        </div>
      </MobileBottomSheet>
    </>
  );
}
